import type { Point, Stroke } from '../save/model.js';
import { deriveSeed, mulberry32 } from '../world/rand.js';

/**
 * Freehand crayon marks.
 *
 * These decorate the car - a scribbled ladder, spots on the door, a squiggle of smoke. Deliberately
 * *not* structural: an earlier plan had her free-draw the wheels, which fails on the one thing the
 * physics depends on. At three she cannot reliably close a circle, so that design needed a whole
 * apparatus to cluster the scribble, take its mean radius and clamp it back into something drivable -
 * elaborate machinery whose only purpose was to hide a task she is not developmentally ready for.
 *
 * Scribbling, on the other hand, is exactly the skill she does have, and for decoration a scribble is
 * already the right answer. Nothing here evaluates or corrects what she drew.
 *
 * Strokes are stored as points, not pixels: a few dozen numbers, resolution-independent, editable
 * later, and they re-render crisply at whatever size the car is baked at.
 */

/** Below this, two points are the same point as far as a crayon is concerned. */
export const MIN_SEGMENT = 2.5;
/** Hard cap per stroke. A long slow drag can otherwise generate thousands of near-identical points. */
export const MAX_POINTS_PER_STROKE = 220;

/**
 * Drop points that are too close together to matter.
 *
 * A pointer at 120Hz produces a cluster of near-identical points, which bloats the save and makes the
 * rendered path lumpy where it should be smooth. Always keeps the first and last point, so the stroke
 * still starts and ends exactly where her finger did.
 */
export function simplify(points: readonly Point[], minSegment = MIN_SEGMENT): readonly Point[] {
  if (points.length <= 2) return points;

  const out: Point[] = [points[0] as Point];
  for (let i = 1; i < points.length - 1; i++) {
    const point = points[i] as Point;
    const last = out[out.length - 1] as Point;
    if (Math.hypot(point.x - last.x, point.y - last.y) >= minSegment) out.push(point);
    if (out.length >= MAX_POINTS_PER_STROKE - 1) break;
  }
  out.push(points[points.length - 1] as Point);
  return out;
}

/**
 * Take one more sample from her finger into a mark that is still being drawn.
 *
 * The same thinning `simplify` does, done as the points arrive instead of once at the end. It has to be
 * incremental: everything downstream of the live array is O(n) - nine rib paths rebuilt into `d` strings
 * and handed to the browser to re-parse, on every pointermove - so an array that grows with the *duration*
 * of the gesture made a long scribble cost O(n²), with the worst of it arriving at the end of the longest
 * marks, on the slowest tablet. Thinned as they arrive, the array grows with the *length* of the line
 * instead, and stops growing altogether at the cap.
 *
 * The last point is always her finger, so the tip of the mark never lags behind it: a sample too close to
 * the one before slides the tip rather than extending the line. Which also means what she watched being
 * drawn is exactly what gets committed - `simplify` at the end now has nothing left to take away, where
 * before it could redraw the whole mark at the moment she lifted off.
 */
export function extendStroke(points: Point[], next: Point): void {
  const anchor = points[points.length - 2];
  const keep =
    anchor !== undefined &&
    points.length < MAX_POINTS_PER_STROKE &&
    Math.hypot(next.x - anchor.x, next.y - anchor.y) >= MIN_SEGMENT;

  /*
   * A sample worth keeping is written twice: once as the point the line now passes through, and once as
   * the tip, which the samples after it will slide around until one of *them* is worth keeping. Writing it
   * only once would leave the previous tip behind as a kept point - and a tip stops wherever her finger
   * happened to be, which is how a "thinned" array ended up with points a fraction of a unit apart.
   */
  points[points.length - 1] = next;
  if (keep) points.push(next);
}

/**
 * An SVG path through the points, using quadratic segments through midpoints.
 *
 * Straight line-to segments would show every twitch of a small unsteady hand as a corner. Curving
 * through the midpoints smooths that out while still following exactly where she drew - it makes the
 * line look like a crayon rather than like a graph.
 */
export function toPathD(points: readonly Point[]): string {
  if (points.length === 0) return '';

  const first = points[0] as Point;
  if (points.length === 1) {
    // A single tap-and-hold: a dot. Drawn as a hairline so the round line cap renders it.
    return `M ${round(first.x)} ${round(first.y)} l 0.01 0`;
  }
  if (points.length === 2) {
    const second = points[1] as Point;
    return `M ${round(first.x)} ${round(first.y)} L ${round(second.x)} ${round(second.y)}`;
  }

  const parts: string[] = [`M ${round(first.x)} ${round(first.y)}`];
  for (let i = 1; i < points.length - 1; i++) {
    const current = points[i] as Point;
    const next = points[i + 1] as Point;
    const midX = (current.x + next.x) / 2;
    const midY = (current.y + next.y) / 2;
    parts.push(`Q ${round(current.x)} ${round(current.y)} ${round(midX)} ${round(midY)}`);
  }
  const last = points[points.length - 1] as Point;
  parts.push(`L ${round(last.x)} ${round(last.y)}`);
  return parts.join(' ');
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/** One slice of a mark: a path, and the width to draw it at. */
export interface Rib {
  readonly d: string;
  readonly width: number;
}

/**
 * How many slices a mark is drawn from.
 *
 * Fixed rather than proportional to the point count, so a long slow scribble costs exactly what a short
 * one does. She can hold a finger down for a while, and the live path is rebuilt on every pointermove.
 */
const RIB_COUNT = 9;
/** Width at the very tip, as a share of the mark's width. */
const TAPER_END = 0.45;
/** How many ribs at each end are tapered. Two is enough to read as a tip rather than a step. */
const TAPER_RIBS = 2;

/**
 * A mark as several overlapping slices of slightly different width.
 *
 * A crayon does not draw a uniform ribbon. One `<path>` at one `stroke-width` does, and it read as vinyl
 * tape stuck on the car - which is what `draw.strokeWidthJitter` was supposed to fix and never did: the
 * tunable promised "a waxy edge" and nothing had ever read it.
 *
 * So the mark is cut into slices, each a little fatter or thinner than the last, and the slices overlap
 * by a shared point so the round caps close the seams. The bulges at the joins *are* the waxy edge. The
 * ends taper, because a crayon lifts off rather than stopping square.
 *
 * `docs/DESIGN.md` gets here by a different route - its Risks table rules out SVG filters on mobile and
 * prescribes "plain round-cap strokes with width jitter" - and this is that, with the taper added.
 *
 * **The jitter is deterministic**, seeded from the mark's own geometry. It has to be: the same stroke is
 * rendered in the studio, again after an undo, again when the car is re-opened from the garage, and again
 * into the baked texture. Anything random would make her mark change shape between those, and a drawing
 * that shifts when you look away is worse than a boring one. Seeding from the points also means nothing
 * new has to be stored in the save.
 */
export function strokeRibs(stroke: Stroke, jitter: number): readonly Rib[] {
  const { points, width } = stroke;
  if (points.length === 0) return [];

  // One rib per segment at most: fewer points than ribs would give empty slices.
  const ribs = Math.max(1, Math.min(RIB_COUNT, points.length - 1));
  const random = mulberry32(seedFor(points));
  const out: Rib[] = [];

  for (let i = 0; i < ribs; i++) {
    const from = Math.floor((i * (points.length - 1)) / ribs);
    // +1 so consecutive slices share a point and the caps overlap instead of leaving a gap.
    const to = Math.ceil(((i + 1) * (points.length - 1)) / ribs);
    const slice = points.slice(from, to + 1);
    if (slice.length === 0) continue;

    const wobble = 1 + (random() * 2 - 1) * jitter;
    out.push({
      d: toPathD(slice),
      width: Math.max(0.5, round(width * wobble * taper(i, ribs))),
    });
  }
  return out;
}

/** 1 through the middle of the mark, easing to `TAPER_END` at either tip. */
function taper(index: number, ribs: number): number {
  if (ribs === 1) return 1;
  const fromEnd = Math.min(index, ribs - 1 - index);
  if (fromEnd >= TAPER_RIBS) return 1;
  return TAPER_END + ((1 - TAPER_END) * fromEnd) / TAPER_RIBS;
}

/**
 * A seed from the shape of the mark itself.
 *
 * Where it *starts*, and which way it set off: cheap, stable across reloads and devices, and different
 * enough between two marks that they do not come out as the same wobble. Two marks begun at the same tenth
 * of a template unit *and* heading the same way would share one, which nobody can see.
 *
 * The far end and the point count used to be in here too, and both of those move while she is drawing - so
 * every rib width was re-rolled on every frame and the whole mark shimmered under her finger, then settled
 * to a different wobble again at commit when the count changed one last time. A mark's jitter is meant to
 * be a property of the mark. These two points are the part of it that is settled by her second frame and
 * never touched again: `extendStroke` only ever moves the tip, and `simplify` keeps both.
 */
function seedFor(points: readonly Point[]): number {
  const first = points[0] as Point;
  const second = points[1] ?? first;
  return deriveSeed(RIB_COUNT, `${round(first.x)},${round(first.y)},${round(second.x)},${round(second.y)}`);
}

/** Total length she actually drew. Used to reject an accidental twitch as a stroke. */
export function strokeLength(points: readonly Point[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1] as Point;
    const b = points[i] as Point;
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

export function strokeBounds(points: readonly Point[]): {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
} | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { minX, minY, maxX, maxY };
}

/**
 * True if any of the stroke passes near a point - used to retire the dashed "draw here" ghost once
 * she has drawn something there. Generous radius: she is three, and being told she missed is not a
 * thing this game does.
 */
export function touchesNear(
  points: readonly Point[],
  target: Point,
  radius: number,
): boolean {
  return points.some((point) => Math.hypot(point.x - target.x, point.y - target.y) <= radius);
}

export function makeStroke(
  points: readonly Point[],
  colour: string,
  width: number,
  regionId: string | null = null,
  anchor: string | null = null,
): Stroke {
  return { points: simplify(points), colour, width, anchor, regionId };
}
